/*
 * Advertorial — editorial behaviours
 * ------------------------------------------------------------------
 * Three small custom elements for the card-based advertorial pages:
 *   <advertorial-reveal>  fades a block in as it scrolls into view
 *   <advertorial-counter> counts a statistic up from zero
 *   <advertorial-sticky>  shows a bottom bar between the hero and the buy box
 *
 * All three are decoration. The rule they share: the page is complete and
 * correct before any of this runs.
 *   - Reveal's hidden state is applied by CSS scoped to `html.js` and to
 *     `prefers-reduced-motion: no-preference`, so a script that never loads, or
 *     a visitor who asked for less motion, gets the article fully visible
 *     rather than a blank column.
 *   - The counter's final value is already in the markup, rendered by Liquid.
 *     The script only replays it from zero once the card scrolls into view, so
 *     a crawler and a no-JS reader both see the real statistic — unlike the
 *     source design, which shipped a literal "0" and let JavaScript fill it in.
 *   - The sticky bar starts off-screen and `visibility: hidden`, which keeps its
 *     link out of the tab order until it is actually on screen.
 */

const REDUCED = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* -------------------------------------------------------------------------- */

class AdvertorialReveal extends HTMLElement {
  connectedCallback() {
    // Nothing is hidden until this runs — see the note on `[data-reveal]` in
    // section-advertorial-editorial.css. Bailing out here leaves the block
    // visible, which is the only safe default for body copy.
    if (REDUCED() || !('IntersectionObserver' in window)) return;

    // Only arm what is still off-screen. Something already in view has been
    // read; hiding it now to fade it back in is a flash, not an entrance — and
    // it is exactly what would happen if the script were held back until the
    // visitor's first interaction.
    const box = this.getBoundingClientRect();
    if (box.top < window.innerHeight && box.bottom > 0) return;

    this.dataset.reveal = 'armed';
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          e.target.dataset.reveal = 'in';
          this.observer.unobserve(e.target);
        }
      },
      { threshold: 0.12 }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

/* -------------------------------------------------------------------------- */

class AdvertorialCounter extends HTMLElement {
  connectedCallback() {
    this.valueEl = this.querySelector('[data-counter-value]');
    if (!this.valueEl) return;

    // `data-to` is the same number Liquid already printed into the text node,
    // so the animation lands exactly where the static markup started.
    this.target = parseFloat(this.dataset.to);
    this.suffix = this.dataset.suffix || '';
    this.decimals = (String(this.dataset.to).split('.')[1] || '').length;

    if (Number.isNaN(this.target) || REDUCED() || !('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          this.observer.unobserve(e.target);
          this.run();
        }
      },
      { threshold: 0.5 }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  run() {
    const DURATION = 900;
    let start = null;
    const step = (ts) => {
      if (start === null) start = ts;
      const k = Math.min((ts - start) / DURATION, 1);
      // Cosine ease, so the number decelerates into its final value instead of
      // stopping dead.
      const eased = 0.5 - Math.cos(Math.PI * k) / 2;
      this.valueEl.textContent = (this.target * eased).toFixed(this.decimals) + this.suffix;
      if (k < 1) requestAnimationFrame(step);
      else this.valueEl.textContent = this.target.toFixed(this.decimals) + this.suffix;
    };
    requestAnimationFrame(step);
  }
}

/* -------------------------------------------------------------------------- */

class AdvertorialSticky extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('.advertorial-sticky');
    if (!this.bar) return;

    this.after = document.querySelector(this.dataset.showAfter || '');
    this.before = document.querySelector(this.dataset.hideAt || '');

    // When the quiz on the page resolves to a size, the bar picks it up, so the
    // visitor's own answer follows them down the page instead of reverting to a
    // generic "from" price.
    this.priceEl = this.querySelector('[data-sticky-price]');
    if (this.priceEl) {
      this.defaultPrice = this.priceEl.textContent;
      this.priceTemplate = this.priceEl.dataset.resultTemplate || '';
      this.onResult = (e) => {
        if (!this.priceTemplate) return;
        this.priceEl.textContent = this.priceTemplate
          .replace('[size]', e.detail.size || '')
          .replace('[price]', e.detail.price || '');
      };
      this.onReset = () => {
        this.priceEl.textContent = this.defaultPrice;
      };
      document.addEventListener('advertorial:quiz-result', this.onResult);
      document.addEventListener('advertorial:quiz-reset', this.onReset);
    }

    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.update();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    if (this.onResult) document.removeEventListener('advertorial:quiz-result', this.onResult);
    if (this.onReset) document.removeEventListener('advertorial:quiz-reset', this.onReset);
    cancelAnimationFrame(this.frame);
  }

  update() {
    // With no anchor to measure against, showing the bar immediately is the
    // safer failure: a merchant who removes the hero still gets their CTA.
    const past = this.after
      ? window.scrollY > this.after.offsetTop + this.after.offsetHeight
      : true;
    // Hidden once the real buy box is on screen, so the page never shows two
    // competing calls to action at once.
    const atBuyBox = this.before
      ? this.before.getBoundingClientRect().top < window.innerHeight
      : false;
    this.bar.classList.toggle('is-visible', past && !atBuyBox);
  }
}

/* -------------------------------------------------------------------------- */

for (const [name, ctor] of [
  ['advertorial-reveal', AdvertorialReveal],
  ['advertorial-counter', AdvertorialCounter],
  ['advertorial-sticky', AdvertorialSticky],
]) {
  if (!customElements.get(name)) customElements.define(name, ctor);
}
